import React, { useRef, useEffect, useCallback } from 'react';
import BordTooltip from 'components/BordTooltip/BordTooltip';
import { COLUMN_TYPE, ITrelloColumn } from './TrelloColumnTypes';
import { BordOneToneIcon } from 'components/NudosComponents';
import LoaderTrelloCard from 'views/shopping/components/LoaderTrelloCard/LoaderTrelloCard';
import { nudosTextcolor } from 'styles/styles.constants';
import './TrelloColumn.scss';

const CONTENT_NO_SCROLL_THRESHOLD = 50;

const hasNoVerticalScroll = (element: HTMLDivElement): boolean => {
  const { scrollHeight, clientHeight } = element;
  return scrollHeight <= clientHeight + CONTENT_NO_SCROLL_THRESHOLD;
};

const getDistanceFromBottom = (element: HTMLDivElement): number => {
  const { scrollTop, scrollHeight, clientHeight } = element;
  return scrollHeight - scrollTop - clientHeight;
};

/**
 * Componente de columna para boards tipo Trello.
 * Utilizado para mostrar columnas en el sistema de tracking de órdenes.
 */
const TrelloColumn = ({
  title,
  subtitle,
  variantIcon,
  items,
  isLoading,
  content,
  renderItem,
  columnType = COLUMN_TYPE.DEFAULT,
  onLoadMore,
  isLoadingMoreItems,
  hasMoreItems,
  trelloColumnHeaderCustomClassName
}: ITrelloColumn) => {
  const columnContentRef = useRef<HTMLDivElement>(null);
  const hasTriggeredLoadNearBottomRef = useRef(false);
  const itemsCount = items?.length || 0;
  const displayCount = itemsCount > 9 ? '9+' : itemsCount;
  const shouldShowTooltip = itemsCount > 9;
  const counterContent = <div className={`counterItemsColumnHeader ${columnType}`}>{displayCount}</div>;
  const LOADER_SKELETON_COUNT = 4;

  const SCROLL_NEAR_BOTTOM_THRESHOLD = 100;
  const SCROLL_AWAY_FROM_BOTTOM_THRESHOLD = 150;
  const CHAIN_LOAD_MORE_DELAY_MS = 100;

  const tryChainLoadMore = useCallback(() => {
    const element = columnContentRef.current;
    if (!element || !onLoadMore || !hasMoreItems || isLoadingMoreItems || isLoading) return;

    const shouldLoadMore =
      hasNoVerticalScroll(element) || getDistanceFromBottom(element) <= SCROLL_NEAR_BOTTOM_THRESHOLD;
    if (shouldLoadMore) onLoadMore();
  }, [onLoadMore, hasMoreItems, isLoadingMoreItems, isLoading]);

  const handleScroll = useCallback(() => {
    if (!columnContentRef.current || !onLoadMore || !hasMoreItems || isLoadingMoreItems) return;
    const distanceFromBottom = getDistanceFromBottom(columnContentRef.current);

    if (distanceFromBottom > SCROLL_AWAY_FROM_BOTTOM_THRESHOLD) {
      hasTriggeredLoadNearBottomRef.current = false;
      return;
    }
    if (distanceFromBottom < SCROLL_NEAR_BOTTOM_THRESHOLD && !hasTriggeredLoadNearBottomRef.current) {
      hasTriggeredLoadNearBottomRef.current = true;
      onLoadMore();
    }
  }, [onLoadMore, hasMoreItems, isLoadingMoreItems]);

  const setupScrollListeners = useCallback(() => {
    const contentElement = columnContentRef.current;
    if (!contentElement) return;

    contentElement.addEventListener('scroll', handleScroll);
    return () => {
      contentElement.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    hasTriggeredLoadNearBottomRef.current = false;
  }, [items?.length, hasMoreItems]);

  useEffect(() => {
    if (!onLoadMore || !hasMoreItems || isLoading || isLoadingMoreItems) return undefined;

    const timerId = setTimeout(tryChainLoadMore, CHAIN_LOAD_MORE_DELAY_MS);
    return () => clearTimeout(timerId);
  }, [items?.length, hasMoreItems, isLoading, isLoadingMoreItems, onLoadMore, tryChainLoadMore]);

  useEffect(() => {
    return setupScrollListeners();
  }, [setupScrollListeners]);

  return (
    <div className="trelloColumn">
      <div className={`trelloColumnHeader ${trelloColumnHeaderCustomClassName}`}>
        <div className="trelloColumnHeaderLeft">
          <div className={`lineColumnHeader ${columnType}`}></div>
          <div className={`iconColumnHeader ${columnType}`}>
            <BordOneToneIcon variant={variantIcon} customWidth="14px" stroke={nudosTextcolor} />
          </div>
          <div className="trelloColumnHeaderContent">
            <div className="trelloColumnTitle">{title}</div>
            <div className="trelloColumnSubtitle">{subtitle}</div>
          </div>
        </div>
        {shouldShowTooltip ? (
          <BordTooltip position="top" message={String(itemsCount)}>
            {counterContent}
          </BordTooltip>
        ) : (
          counterContent
        )}
      </div>
      <div ref={columnContentRef} className="trelloColumnContent" onScroll={handleScroll}>
        {isLoading
          ? Array.from({ length: LOADER_SKELETON_COUNT }).map((_, index) => (
              <LoaderTrelloCard key={`loader-${index}`} />
            ))
          : content !== undefined
          ? content
          : items && items?.length > 0 && renderItem
          ? items?.map((item, index) => (
              <React.Fragment key={`itemOrder-${index}-${item.orderId}`}>{renderItem(item)}</React.Fragment>
            ))
          : null}
        {isLoadingMoreItems && <LoaderTrelloCard key="loader-more" />}
      </div>
    </div>
  );
};

export default TrelloColumn;
